<script setup>
const props = defineProps({
  label: {
    type: String,
    default: '',
  },
  modelValue: {
    type: [String, Number],
    default: '',
  },
  options: {
    type: Array,
    required: true,
    // Array of strings or objects with { value, label }
  },
  placeholder: {
    type: String,
    default: 'בחר...',
  },
  required: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['update:modelValue'])

function handleChange(e) {
  emit('update:modelValue', e.target.value)
}

function getOptionValue(option) {
  return typeof option === 'object' ? option.value : option
}

function getOptionLabel(option) {
  return typeof option === 'object' ? option.label : option
}
</script>

<template>
  <div class="form-select" :class="{ 'has-error': error }">
    <label v-if="label" class="field-label">
      {{ label }}
      <span v-if="required" class="required-mark">*</span>
    </label>
    <div class="select-wrapper">
      <select :value="modelValue" :disabled="disabled" class="field-select" @change="handleChange">
        <option value="" disabled>{{ placeholder }}</option>
        <option
          v-for="option in options"
          :key="getOptionValue(option)"
          :value="getOptionValue(option)"
        >
          {{ getOptionLabel(option) }}
        </option>
      </select>
      <span class="select-arrow material-symbols-rounded">expand_more</span>
    </div>
    <span v-if="error" class="field-error">{{ error }}</span>
  </div>
</template>

<style scoped>
.form-select {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.field-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
}

.required-mark {
  color: var(--color-error);
  margin-right: 2px;
}

.select-wrapper {
  position: relative;
}

.field-select {
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  padding-left: calc(var(--spacing-md) + 24px);
  font-size: var(--font-size-base);
  font-family: inherit;
  color: var(--text-primary);
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  transition: all var(--transition-fast);
}

.field-select:hover:not(:disabled) {
  border-color: var(--color-gray-300);
}

.field-select:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.field-select:disabled {
  background: var(--bg-secondary);
  color: var(--text-muted);
  cursor: not-allowed;
}

.select-arrow {
  position: absolute;
  left: var(--spacing-sm);
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
  pointer-events: none;
  font-size: 20px;
  transition: transform var(--transition-fast);
}

.field-select:focus + .select-arrow {
  color: var(--color-primary);
}

.has-error .field-select {
  border-color: var(--color-error);
}

.has-error .field-select:focus {
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.field-error {
  font-size: var(--font-size-xs);
  color: var(--color-error);
}
</style>
